import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  formatMatrikkel,
  useRouteDocumentTitle,
} from '@client/toolkit';

import {
  BuildingFilterPanel,
  EnergyBadge,
  KpiTile,
  PLACEHOLDER,
  ReportTableSkeletonRows,
  formatNumber,
  formatPercent,
  useBuildingFilter,
} from '../components/portfolio-report';
import { ReportingPaths } from '../reporting.router';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { YearPicker } from '../../../components';
import { useYearParam } from '../../../hooks';
import { PortfolioOverviewBuilding, useGetReportingPortfolioOverviewQuery } from '@client/api';

// Datakvalitet: a building counts as "complete" when the core matrikkel/registry fields are present.
function getMissingDataFields(b: PortfolioOverviewBuilding): string[] {
  const checks: Array<[boolean, string]> = [
    [!!formatMatrikkel(b), 'matrikkel'],
    [!!b.type, 'type'],
    [b.grossAreaM2 > 0, 'area'],
    [b.constructionYear != null, 'constructionYear'],
    [!!b.energyLabel, 'energyLabel'],
    [b.owners.length > 0, 'owner'],
  ];
  return checks.filter(([ok]) => !ok).map(([, key]) => key);
}

export function ReportingPortfolioOverview2Route() {
  const { t } = useTranslation('reporting');
  const { t: tType } = useTranslation('type');
  useRouteDocumentTitle(t('portfolioOverview2.documentTitle'), ReportingPaths.PortfolioOverview2);

  const { selectedYear, setSelectedYear } = useYearParam();

  // The financial figures follow the selected year; the register itself is year-agnostic. We use
  // isFetching (not isLoading) so skeletons also show while refetching after a year change.
  const { data, isFetching } = useGetReportingPortfolioOverviewQuery({
    startDate: selectedYear.startOf('year').format('YYYY-MM-DD'),
    endDate: selectedYear.endOf('year').format('YYYY-MM-DD'),
  });

  const buildings = useMemo(() => data?.buildings ?? [], [data]);
  const { buildingFilter, setBuildingFilter, activeTags, toggleTag, allTags, filtered } = useBuildingFilter(buildings);

  const translateType = (type: string | null | undefined): string =>
    type ? tType(`BuildingType.${type}`, { defaultValue: type }) : PLACEHOLDER;

  // KPI derivations. All KPIs follow the active filters (building search + tags) so the four
  // boxes always describe the same set shown in the register below.
  const buildingCount = filtered.length;
  // Total area uses each building's own gross area (BRA), not the rentable-space sum.
  const totalArea = useMemo(() => filtered.reduce((sum, b) => sum + (b.grossAreaM2 || 0), 0), [filtered]);

  const municipalityCount = useMemo(
    () => new Set(filtered.map((b) => b.city).filter((city): city is string => !!city)).size,
    [filtered],
  );

  const segmentConcentration = useMemo(() => {
    if (filtered.length === 0) return null;
    const areaByType = new Map<string, number>();
    let sum = 0;
    filtered.forEach((b) => {
      const key = b.type ?? 'unknown';
      areaByType.set(key, (areaByType.get(key) ?? 0) + (b.grossAreaM2 || 0));
      sum += b.grossAreaM2 || 0;
    });
    if (sum === 0) return null;
    return (Math.max(...areaByType.values()) / sum) * 100;
  }, [filtered]);

  const filterPanel = (
    <BuildingFilterPanel
      title={t('portfolioOverview2.filter.title')}
      searchPlaceholder={t('portfolioOverview2.filter.building')}
      noTagsLabel={t('portfolioOverview2.filter.noTags')}
      buildingFilter={buildingFilter}
      onBuildingFilterChange={setBuildingFilter}
      allTags={allTags}
      activeTags={activeTags}
      onToggleTag={toggleTag}
      action={<YearPicker value={selectedYear} onChange={setSelectedYear} />}
    />
  );

  return (
    <ReportPageContent breadcrumb={t('portfolioOverview2.breadcrumb')} menu={filterPanel}>
      <div className="flex gap-5 justify-between h-[146px] text-white">
        <KpiTile
          label={t('portfolioOverview2.kpi.buildings')}
          sub={t('portfolioOverview2.kpi.buildingsSub')}
          value={formatNumber(buildingCount)}
          loading={isFetching}
        />
        <KpiTile
          label={t('portfolioOverview2.kpi.geography')}
          sub={t('portfolioOverview2.kpi.geographySub')}
          value={formatNumber(municipalityCount)}
          loading={isFetching}
        />
        <KpiTile
          label={t('portfolioOverview2.kpi.totalArea')}
          sub={t('portfolioOverview2.kpi.totalAreaSub')}
          value={`${formatNumber(totalArea)} m²`}
          loading={isFetching}
        />
        <KpiTile
          label={t('portfolioOverview2.kpi.segment')}
          sub={t('portfolioOverview2.kpi.segmentSub')}
          value={formatPercent(segmentConcentration)}
          loading={isFetching}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle variant="highlighted">{t('portfolioOverview2.register.title')}</CardTitle>
        </CardHeader>
        <CardContent variant="fill" className="px-4 pb-4">
          <Table className="w-full" size="small">
            <TableHead>
              <TableHeadRow>
                <TableHeadCell>{t('portfolioOverview2.register.header.building')}</TableHeadCell>
                <TableHeadCell>{t('portfolioOverview2.register.header.municipality')}</TableHeadCell>
                <TableHeadCell>{t('portfolioOverview2.register.header.matrikkel')}</TableHeadCell>
                <TableHeadCell>{t('portfolioOverview2.register.header.type')}</TableHeadCell>
                <TableHeadCell align="right">{t('portfolioOverview2.register.header.area')}</TableHeadCell>
                <TableHeadCell align="right">{t('portfolioOverview2.register.header.year')}</TableHeadCell>
                <TableHeadCell>{t('portfolioOverview2.register.header.energy')}</TableHeadCell>
                <TableHeadCell>{t('portfolioOverview2.register.header.owner')}</TableHeadCell>
                <TableHeadCell>{t('portfolioOverview2.register.header.quality')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {isFetching ? (
                <ReportTableSkeletonRows columns={9} />
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <span className="text-white/60">{t('portfolioOverview2.register.empty')}</span>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((b) => {
                  const missing = getMissingDataFields(b);
                  return (
                    <TableRow key={b.id}>
                      <TableCell>
                        <Link
                          to={`/portfolio/buildings/${b.id}`}
                          className="font-medium text-white hover:underline focus:outline-none focus:underline"
                        >
                          {b.name}
                        </Link>
                      </TableCell>
                      <TableCell>{b.city || b.address || PLACEHOLDER}</TableCell>
                      <TableCell>{formatMatrikkel(b) ?? PLACEHOLDER}</TableCell>
                      <TableCell>{translateType(b.type)}</TableCell>
                      <TableCell align="right">{formatNumber(b.grossAreaM2)}</TableCell>
                      <TableCell align="right">{b.constructionYear ?? PLACEHOLDER}</TableCell>
                      <TableCell>
                        <EnergyBadge label={b.energyLabel ?? null} />
                      </TableCell>
                      <TableCell>
                        <span className="truncate block max-w-[160px]" title={b.owners.join(', ')}>
                          {b.owners.length > 0 ? b.owners.join(', ') : PLACEHOLDER}
                        </span>
                      </TableCell>
                      <TableCell>
                        {missing.length === 0 ? (
                          <span className="text-emerald-300" title={t('portfolioOverview2.register.complete')}>
                            ✓
                          </span>
                        ) : (
                          <span className="text-rose-300" title={missing.join(', ')}>
                            {t('portfolioOverview2.register.missing')}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </ReportPageContent>
  );
}
